import { buildLegalPagePath } from "@/lib/legal-page-path";
import { buildLocalizedPagePath } from "@/lib/localized-page-path";
import { buildArtistHubPath } from "@garimpa/lib/buildArtistHubPath";
import { buildFeaturePageLandingPath } from "@garimpa/lib/buildFeaturePageLandingPath";
import { CURATED_CRATES, type CuratedCrate } from "@garimpa/lib/curatedCrates";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

export type FooterGroupId = "deals" | "garimpos" | "alerts" | "explore" | "about";
export type FooterLinkId =
  | "offers"
  | "vinylDeals"
  | "cdDeals"
  | "alerts"
  | "telegram"
  | "whatsapp"
  | "editorial"
  | "collections"
  | "stories"
  | "genres"
  | "styles"
  | "artists"
  | "stores"
  | "providers"
  | "contact"
  | "privacy"
  | "terms";

export interface FooterLink {
  id: FooterLinkId;
  to: string;
  search?: Record<string, string>;
  hoverClassName?: string;
}

/** Sitewide footer links. Every target is indexable and listed in a sitemap. */
export function buildFooterLinkGroups(
  locale: ViewerLocale,
): Array<{ id: FooterGroupId; links: FooterLink[] }> {
  return [
    {
      id: "deals",
      links: [
        { id: "offers", to: "/offers" },
        { id: "vinylDeals", to: "/offers", search: { format: "vinyl" } },
        { id: "cdDeals", to: "/offers", search: { format: "cd" } },
      ],
    },
    {
      id: "alerts",
      links: [
        { id: "alerts", to: "/alerts" },
        { id: "telegram", to: "/telegram", hoverClassName: "hover:text-[#229ED9]" },
        { id: "whatsapp", to: "/whatsapp", hoverClassName: "hover:text-[#25D366]" },
      ],
    },
    {
      id: "explore",
      links: [
        { id: "editorial", to: buildLocalizedPagePath(locale, "editorial") },
        { id: "collections", to: buildFeaturePageLandingPath(locale, "collection") },
        { id: "stories", to: buildFeaturePageLandingPath(locale, "story") },
        { id: "genres", to: buildFeaturePageLandingPath(locale, "genre") },
        { id: "styles", to: buildFeaturePageLandingPath(locale, "style") },
        { id: "artists", to: buildArtistHubPath(locale) },
      ],
    },
    {
      id: "about",
      links: [
        { id: "stores", to: buildLocalizedPagePath(locale, "stores") },
        { id: "providers", to: buildLocalizedPagePath(locale, "about", "providers") },
        { id: "contact", to: buildLocalizedPagePath(locale, "contact") },
        { id: "privacy", to: buildLegalPagePath(locale, "privacy") },
        { id: "terms", to: buildLegalPagePath(locale, "terms") },
      ],
    },
  ];
}

/** Curated crates picked for the footer's Garimpos group, plus the hub link. */
export function buildFooterCrateLinks(locale: ViewerLocale): {
  crates: CuratedCrate[];
  hubPath: string;
} {
  return {
    crates: CURATED_CRATES.filter((crate) => crate.footer),
    hubPath: buildLocalizedPagePath(locale, "garimpos"),
  };
}
